import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brand } from '../../components/Brand';
import {
  questionDepth,
  type EntityQuestionValue,
} from '../../lib/entities/entityQuestions';
import Logo from '../../components/Logo';
import { ArrowRight, Tick } from '../components/AppIcons';
import {
  COMPANY_TYPES,
  createCompany,
  type CompanyRegion,
  type CompanyType,
} from '../../lib/entities/companies';
import {
  answerQuestion,
  getGroupedQuestions,
  getNextQuestion,
  getOnboardingState,
  updateUserProfile,
  type NextQuestion,
  type OnboardingQuestion,
  type QuestionGroup,
} from '../../lib/entities/onboarding';
import {
  lookupCompanyNumber,
  lookupMessage,
  type RegisterProfile,
} from '../../lib/entities/registers';
import { canDevSeed, devSeedOnboarding } from '../devSeed';
import '../../styles/app.css';
import '../../styles/app-company.css';

/**
 * The guided, resumable onboarding wizard (≤2 fields per screen).
 *
 * It drives entirely off the backend: GET /api/onboarding/state decides the
 * starting screen, PUT /api/users/me persists the profile, POST /api/companies
 * creates the company ONCE (name+type+number+region+headcount in a single call),
 * then the findings loop chains GET/POST onboarding questions until done.
 *
 * A completed user who lands here is bounced straight to /app.
 */

type Phase =
  | 'loading'
  | 'loadError'
  | 'name'
  | 'company'
  | 'companyNumber'
  | 'regionHeadcount'
  | 'findings'
  | 'groups'
  | 'done';

const REGION_OPTIONS: { value: CompanyRegion; label: string; sub: string }[] = [
  { value: 'england-wales', label: 'England or Wales', sub: 'The default for most UK businesses.' },
  { value: 'scotland', label: 'Scotland', sub: 'Some employment rules differ here.' },
];

/** The three high-level progress groups the dots track. */
const GROUP_LABELS = ['About you', 'Your company', 'Your business'];

function phaseGroup(phase: Phase): number {
  switch (phase) {
    case 'name':
      return 0;
    case 'company':
    case 'companyNumber':
      return 1;
    case 'regionHeadcount':
    case 'findings':
    case 'groups':
      return 2;
    default:
      return 3; // loading / done — nothing to highlight
  }
}

function StepDots({ group }: { group: number }) {
  return (
    <div className="co-ob__dots" aria-label={`Step ${Math.min(group + 1, GROUP_LABELS.length)} of ${GROUP_LABELS.length}`}>
      {GROUP_LABELS.map((label, i) => (
        <span
          key={label}
          className={`co-ob__dot${i === group ? ' is-active' : ''}${i < group ? ' is-done' : ''}`}
        />
      ))}
    </div>
  );
}

/**
 * Human name for the register a suggestion came from. Named honestly: the user is confirming this value
 * by leaving it, so attributing an FCA finding to Companies House would misstate the very thing the
 * provenance line exists to convey.
 */
function registerName(source: string | null | undefined): string {
  switch (source) {
    case 'fca':
      return 'the FCA register';
    case 'companies-house':
      return 'Companies House';
    default:
      return 'a public register';
  }
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export default function OnboardingPage() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Synchronous re-entry lock for the awaited submits. `submitting` state alone can't prevent a
  // fast second click (it hasn't re-rendered yet) — this ref closes that window, so a company is
  // POSTed at most once even under a double-click or a retry.
  const submitLock = useRef(false);

  // Resumption facts from onboarding state (used after the profile submit).
  const [companyCount, setCompanyCount] = useState(0);
  const [resumeCompanyId, setResumeCompanyId] = useState<string | null>(null);

  // Section A/B — the person.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');

  // Section C/D/C2 — the company (collected across screens, created once at C2).
  const [companyName, setCompanyName] = useState('');
  const [companyType, setCompanyType] = useState<CompanyType | ''>('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [region, setRegion] = useState<CompanyRegion>('england-wales');
  // Employees only — 0 is valid (a sole trader with no staff employs nobody). Not "people at the
  // company": the owner/directors don't count, so this stays comparable to the employee-list total.
  const [headcount, setHeadcount] = useState(0);

  // Section E — the findings loop.
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Companies House lookup. `lookedUp` is what the register told us, kept so the confirm screen can cite
  // its source; `lookupNote` is the plain-English reason a lookup produced nothing. A failed lookup NEVER
  // blocks onboarding — every path falls through to filling the form in by hand.
  const [lookedUp, setLookedUp] = useState<RegisterProfile | null>(null);
  const [lookupNote, setLookupNote] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  // What the FCA register said, carried to company-create so the question screens can suggest the
  // regulated-firm answers. NULL means "could not establish" and must never behave like a no.
  const [fcaStatus, setFcaStatus] = useState<string | null>(null);
  // The same id, but readable straight away. setState is queued, so a function called in the SAME
  // tick as setCompanyId still closes over the old null: that silently stranded the resume path on
  // the loading screen, because the grouped phase bailed out before setting any phase at all.
  const activeCompanyId = useRef<string | null>(null);
  const [question, setQuestion] = useState<OnboardingQuestion | null>(null);
  const [answer, setAnswer] = useState('');
  // Questions already answered in this run, most recent last, each with the answer that was given.
  // The backend only ever serves the NEXT unanswered question, so stepping back is a client concern:
  // this is what lets someone undo a mis-click instead of living with it.
  const [answered, setAnswered] = useState<{ question: OnboardingQuestion; answer: string }[]>([]);

  // The grouped predicate phase. `groups` is the server's view of what applies right now, refetched
  // after every answer so a gate reveals its children in place. `groupIndex` is the screen we are on.
  const [groups, setGroups] = useState<QuestionGroup[]>([]);
  const [groupIndex, setGroupIndex] = useState(0);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  // Only questions that JUST appeared animate in. Animating the whole screen on arrival would be
  // motion for its own sake; the point is to show where a new question came from.
  const seenKeys = useRef<Set<string> | null>(null);
  const [revealing, setRevealing] = useState<Set<string>>(new Set());
  // Questions that have just STOPPED applying. React would unmount them instantly, so they are held
  // for the length of the exit animation, at the position they occupied, then dropped. Without this
  // a follow-up simply blinks out of existence, which is exactly as jarring as it appearing did.
  const prevGroups = useRef<QuestionGroup[] | null>(null);
  const [exiting, setExiting] = useState<
    { key: string; index: number; group: string; question: EntityQuestionValue }[]
  >([]);

  // Decide the starting screen from onboarding state (resumption).
  useEffect(() => {
    let cancelled = false;
    getOnboardingState()
      .then(async (state) => {
        if (cancelled) return;
        if (state.complete) {
          navigate('/app', { replace: true });
          return;
        }
        setCompanyCount(state.companyCount);
        setResumeCompanyId(state.primaryCompanyId);

        if (!state.userProfileComplete) {
          setPhase('name');
          return;
        }
        // Profile already done — resume at the company block or the findings loop.
        if (state.companyCount > 0 && state.primaryCompanyId) {
          activeCompanyId.current = state.primaryCompanyId;
          setCompanyId(state.primaryCompanyId);
          try {
            const nxt = await getNextQuestion(state.primaryCompanyId);
            if (cancelled) return;
            advanceFindings(nxt);
          } catch (err) {
            if (cancelled) return;
            // State read fine but the next question didn't load — a retryable blip. Show a retry
            // rather than dumping a fully-profiled user back onto the name screen to re-type.
            setError(errorText(err, 'Could not load your onboarding. Please try again.'));
            setPhase('loadError');
          }
        } else {
          setPhase('company');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(errorText(err, 'Could not start onboarding. Please try again.'));
        setPhase('loadError');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const current = new Set(groups.flatMap((g) => g.questions.map((q) => q.key)));
    // First population seeds the baseline silently: nothing on the opening screen is "new".
    if (seenKeys.current === null) {
      seenKeys.current = current;
      return;
    }
    const fresh = [...current].filter((k) => !seenKeys.current!.has(k));
    seenKeys.current = current;
    setRevealing(fresh.length ? new Set(fresh) : new Set());

    // Anything that was on a screen and no longer is, recorded with where it sat so it can animate
    // out in place rather than jumping to the end of the list first.
    const previous = prevGroups.current;
    if (previous) {
      const gone: typeof exiting = [];
      for (const before of previous) {
        const after = groups.find((g) => g.key === before.key);
        const stillThere = new Set(after ? after.questions.map((q) => q.key) : []);
        before.questions.forEach((question, index) => {
          if (!stillThere.has(question.key)) {
            gone.push({ key: question.key, index, group: before.key, question });
          }
        });
      }
      if (gone.length) setExiting(gone);
    }
  }, [groups]);

  // Seeded separately from `seenKeys` so the removal pass above never runs against a half-built
  // baseline on the very first load.
  useEffect(() => {
    prevGroups.current = groups;
  }, [groups]);

  // Drop the held questions once the animation has played. Its own effect for the same reason the
  // reveal timer is: folded in above, an unrelated refetch would cancel the pending clear.
  useEffect(() => {
    if (exiting.length === 0) return;
    const timer = setTimeout(() => setExiting([]), 200);
    return () => clearTimeout(timer);
  }, [exiting]);

  // Clearing is its own effect, keyed off `revealing` rather than `groups`. Folded into the effect
  // above, an unrelated refetch re-runs it, the cleanup cancels the pending clear, and the early
  // return leaves the class stuck on those questions for good, ready to replay on any later render.
  useEffect(() => {
    if (revealing.size === 0) return;
    const timer = setTimeout(() => setRevealing(new Set()), 400);
    return () => clearTimeout(timer);
  }, [revealing]);

  // When the wizard finishes, land in the app (replace so Back doesn't re-onboard).
  useEffect(() => {
    if (phase === 'done') navigate('/app', { replace: true });
  }, [phase, navigate]);

  /** Move into the findings loop from a next-question response (or finish). */
  function advanceFindings(nxt: NextQuestion) {
    if (nxt.done || !nxt.question) {
      setPhase('done');
      return;
    }
    // A question carrying a group belongs to a capture SCREEN, so the wizard stops asking one at a
    // time and switches to the grouped phase. The free-text profile questions have no group and keep
    // the single-question treatment.
    if (nxt.question.group) {
      void enterGroups();
      return;
    }
    setQuestion(nxt.question);
    setAnswer('');
    setPhase('findings');
  }

  /** Load the capture screens and show the first one that still has an unanswered question. */
  async function enterGroups() {
    const id = activeCompanyId.current ?? companyId ?? resumeCompanyId;
    if (!id) {
      // Should not happen: the grouped phase is only reachable once a company exists. Surfacing it
      // beats the previous silent return, which left the wizard on "Getting things ready…" forever.
      setError('Could not work out which company to set up. Please reload.');
      setPhase('loadError');
      return;
    }
    try {
      const res = await getGroupedQuestions(id);
      if (res.groups.length === 0) {
        setPhase('done');
        return;
      }
      setGroups(res.groups);
      const firstUnfinished = res.groups.findIndex((g) =>
        g.questions.some((q) => typeof q.value !== 'boolean'),
      );
      setGroupIndex(firstUnfinished === -1 ? res.groups.length - 1 : firstUnfinished);
      setPhase('groups');
    } catch (err) {
      setError(errorText(err, 'Could not load your questions. Please try again.'));
      setPhase('loadError');
    }
  }

  /**
   * The value SHOWN for a question: the stored answer, or the register's suggestion when there is none.
   * Founder's ruling: a suggestion the user is shown and leaves alone counts as their answer — which is
   * safe precisely because it is visible, sourced and changeable, unlike a hidden default.
   */
  function shownValue(q: EntityQuestionValue): boolean | null {
    return typeof q.value === 'boolean' ? q.value : (q.suggestedValue ?? null);
  }

  /**
   * Persist the suggestions on this screen that the user left as they were, then refetch.
   *
   * Order matters: the list is already parents-before-children, and the server drops a child whose parent
   * is not true in the stored map, so saving in array order is what makes a suggested parent+child pair
   * (agency-business + supply-temp-workers) land instead of silently half-landing.
   */
  async function commitShownSuggestions(): Promise<QuestionGroup[] | null> {
    const id = activeCompanyId.current ?? companyId ?? resumeCompanyId;
    const screen = groups[groupIndex];
    if (!id || !screen) return null;

    const pending = screen.questions.filter(
      (q) => typeof q.value !== 'boolean' && typeof q.suggestedValue === 'boolean',
    );
    if (pending.length === 0) return null;

    for (const q of pending) {
      await answerQuestion(id, q.key, String(q.suggestedValue));
    }
    // One refetch after the batch: a committed suggestion can open a gate and reveal new questions.
    // RETURNED, not just set: setGroups is async, so the caller cannot re-read `groups` to see this.
    const refreshed = (await getGroupedQuestions(id)).groups;
    setGroups(refreshed);
    return refreshed;
  }

  /**
   * Answer one question on a screen. Persists immediately (so an abandoned run keeps what was given),
   * then refetches the screens: the answer may have unlocked children, and the server has re-sanitized
   * away any that a "no" just orphaned.
   */
  async function answerGrouped(key: string, value: boolean) {
    const id = activeCompanyId.current ?? companyId ?? resumeCompanyId;
    if (!id || savingKey) return;
    setSavingKey(key);
    // Optimistic, so the toggle responds instantly rather than after a round trip.
    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        questions: g.questions.map((q) => (q.key === key ? { ...q, value } : q)),
      })),
    );
    try {
      await answerQuestion(id, key, String(value));
      const res = await getGroupedQuestions(id);
      setGroups(res.groups);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not save your answer. Try again.'));
    } finally {
      setSavingKey(null);
    }
  }

  /**
   * Step back to the previous question of this run, pre-filled with what was answered. The answer
   * stays saved server-side until it is changed: re-answering overwrites it, and because the server
   * always serves the next UNANSWERED question, continuing from here jumps forward to wherever the
   * run had got to rather than replaying everything in between.
   */
  function goBackOneQuestion() {
    setAnswered((prev) => {
      const last = prev[prev.length - 1];
      if (!last) return prev;
      setError(null);
      setQuestion(last.question);
      setAnswer(last.answer);
      return prev.slice(0, -1);
    });
  }

  // Back is only offered within a not-yet-persisted screen group.
  const backPhase: Phase | null =
    phase === 'company'
      ? 'companyNumber'
      : phase === 'regionHeadcount'
        ? 'company'
        : null;

  // Findings has its own Back: it steps through answered questions rather than between screens, and
  // it stops at the first one. It deliberately does NOT fall through to regionHeadcount, because
  // that screen's Continue creates the company, which has already happened by now.
  const canGoBackAQuestion = phase === 'findings' && answered.length > 0;

  // Within the grouped phase, Back steps to the previous SCREEN. It stops at the first one rather
  // than falling back into the single-question phase, whose questions are already answered.
  const canGoBackAScreen = phase === 'groups' && groupIndex > 0;

  // What the current screen actually renders: the questions that apply, plus any that just stopped
  // applying, put back where they were so they animate out from their own position.
  const leaving = new Set(
    exiting.filter((e) => e.group === groups[groupIndex]?.key).map((e) => e.key),
  );
  const screenQuestions = (() => {
    const list = [...(groups[groupIndex]?.questions ?? [])];
    for (const e of exiting) {
      if (e.group !== groups[groupIndex]?.key) continue;
      list.splice(Math.min(e.index, list.length), 0, e.question);
    }
    return list;
  })();

  const canContinue = (() => {
    switch (phase) {
      case 'name':
        return firstName.trim().length > 0 && lastName.trim().length > 0 && role.trim().length > 0;
      case 'company':
        return companyName.trim().length > 0 && companyType !== '';
      case 'companyNumber':
        // Continue means "look this up", so it needs something to look up. Having no number is a
        // first-class path, taken through the two buttons below the card, not by continuing blank.
        return companyNumber.trim().length > 0;
      case 'regionHeadcount':
        return headcount >= 0; // 0 employees is valid (sole trader); region always has a default
      case 'findings':
        return answer.trim().length > 0;
      case 'groups': {
        const screen = groups[groupIndex];
        // Same rule as everywhere else: an applicable question must have an explicit yes or no. Absent
        // is "no answer", which would mis-tier every obligation it gates. A register suggestion counts,
        // because it is SHOWN as the selected value — leaving it is a choice, and Continue commits it.
        return !!screen && screen.questions.every((q) => typeof shownValue(q) === 'boolean');
      }
      default:
        return false;
    }
  })();

  /**
   * Look the number up and move on to the company screen either way.
   *
   * A hit PRE-FILLS the company fields, which the user then reviews and submits — so what is stored is
   * user-submitted data, not a register value written behind their back. A miss (bad number, unknown
   * company, register down, no key) just leaves the fields blank with a note explaining why. Nothing is
   * ever assumed from a failure.
   */
  async function lookUpCompanyNumber() {
    if (lookingUp) return;
    setLookingUp(true);
    setError(null);
    setLookupNote(null);
    try {
      const result = await lookupCompanyNumber(companyNumber.trim());
      if (result.outcome === 'found' && result.profile) {
        const p = result.profile;
        setLookedUp(p);
        if (p.name) setCompanyName(p.name);
        // Null means the register did not say, so leave the user to choose rather than guessing.
        if (p.companyType) setCompanyType(p.companyType);
        if (p.region) setRegion(p.region);
        if (result.companyNumber) setCompanyNumber(result.companyNumber); // normalised form
        setFcaStatus(result.fcaStatus ?? null);
      } else {
        setLookedUp(null);
        setFcaStatus(null);
        setLookupNote(lookupMessage(result.outcome));
      }
    } catch {
      // Even an unexpected client-side failure degrades to asking.
      setLookedUp(null);
      setFcaStatus(null);
      setLookupNote('Couldn’t reach Companies House. Carry on and set things up by hand.');
    } finally {
      setLookingUp(false);
      setPhase('company');
    }
  }

  /** Skip the lookup entirely: sole traders and partnerships have no number to look up. */
  function skipLookup() {
    setCompanyNumber('');
    setLookedUp(null);
    setFcaStatus(null);
    setLookupNote(null);
    setError(null);
    setPhase('company');
  }

  async function submitProfile() {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      await updateUserProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role: role.trim(),
      });
      // Company block is skipped when one already exists — jump to findings.
      if (companyCount > 0 && resumeCompanyId) {
        setCompanyId(resumeCompanyId);
        advanceFindings(await getNextQuestion(resumeCompanyId));
      } else {
        // The company number comes FIRST: with it, Companies House gives us the legal name, the legal
        // form and the jurisdiction, so the next screen is a confirmation rather than a blank form.
        setPhase('companyNumber');
      }
    } catch (err) {
      setError(errorText(err, 'Could not save your details. Try again.'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  }

  async function submitCompany() {
    if (companyType === '' || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      // If a prior attempt already created the company (create succeeded but the follow-on
      // next-question fetch failed), do NOT POST a second one — resume the findings loop against it.
      if (companyId) {
        advanceFindings(await getNextQuestion(companyId));
        return;
      }
      const created = await createCompany({
        name: companyName.trim(),
        companyType,
        // Send whatever number the user gave, whatever legal form they ended on. The old
        // `isRegisteredCompanyType` guard here predates the lookup, when the number screen only
        // appeared for Ltd/LLP/PLC; now the number is collected FIRST, so that guard would silently
        // discard a valid number from a charity (a CIO has one) or from any form Companies House could
        // not map. The number is the join key for every later register lookup, so it must survive.
        companyNumber: companyNumber.trim() || undefined,
        // Register FACTS from the lookup, carried through so the question screens can suggest answers
        // with their source shown. Not answers: the suggestions are rendered, not stored.
        sicCodes: lookedUp?.sicCodes,
        lastAccountsType: lookedUp?.lastAccountsType ?? undefined,
        fcaStatus: fcaStatus ?? undefined,
        region,
        declaredHeadcount: headcount,
      });
      activeCompanyId.current = created.id;
      setCompanyId(created.id);
      advanceFindings(await getNextQuestion(created.id));
    } catch (err) {
      setError(errorText(err, 'Could not create your company. Try again.'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  }

  async function submitAnswer() {
    if (!companyId || !question || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    const asked = question;
    const given = answer.trim();
    try {
      const next = await answerQuestion(companyId, asked.key, given);
      // Only recorded once the answer is actually saved, so Back can never offer to revisit a
      // question whose answer never landed.
      setAnswered((prev) => [...prev, { question: asked, answer: given }]);
      advanceFindings(next);
    } catch (err) {
      setError(errorText(err, 'Could not save your answer. Try again.'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  }

  async function onContinue() {
    if (submitting || !canContinue) return;
    setError(null);
    switch (phase) {
      case 'name':
        await submitProfile();
        break;
      case 'companyNumber':
        await lookUpCompanyNumber();
        break;
      case 'company':
        setPhase('regionHeadcount');
        break;
      case 'regionHeadcount':
        await submitCompany();
        break;
      case 'findings':
        await submitAnswer();
        break;
      case 'groups': {
        // Commit anything the user was shown and left, THEN check the screen again: committing a
        // suggestion can open a gate (a hazardous SIC opens the hazards gate), and the questions it
        // reveals are on this same screen. Advancing blind would skip them.
        const refreshedGroups = await commitShownSuggestions();
        const screen = (refreshedGroups ?? groups)[groupIndex];
        if (screen?.questions.some((q) => typeof shownValue(q) !== 'boolean')) break;

        if (groupIndex < groups.length - 1) {
          setGroupIndex((n) => n + 1);
        } else {
          setPhase('done');
        }
        break;
      }
    }
  }

  /**
   * Dev-only: answer everything that's left with defaults and go straight to the app. Runs the same
   * endpoints the wizard does (see devSeed.ts), so it exercises the real path rather than faking a
   * finished company. Passing the current companyId means using it from a later screen reuses the
   * company already created instead of making a second one.
   */
  async function runDevSeed() {
    setSubmitting(true);
    setError(null);
    try {
      await devSeedOnboarding(companyId ?? undefined);
      setPhase('done');
    } catch (err) {
      setError(errorText(err, 'The demo shortcut failed.'));
      setSubmitting(false);
    }
  }

  const group = phaseGroup(phase);
  const isFormPhase = phase !== 'loading' && phase !== 'done' && phase !== 'loadError';

  return (
    <div className="co-ob">
      <div className="co-ob__logo">
        <Logo height={32} />
      </div>

      <div className="co-ob__col">
        {/*
          Outside the card, and above the progress dots. It is context for the whole run of questions
          rather than part of the one being asked, and it stays up for all of them: "why am I being
          asked this?" is a thought someone has at question nine, not question one.
        */}
        {(phase === 'findings' || phase === 'groups') && (
          <p className="co-ob__lede">
            These answers decide which policies and records apply to a business like yours, so your
            requirements only show what you genuinely need. They are also what lets <Brand /> answer
            about your business rather than in general. You can change any of them later.
          </p>
        )}

        {isFormPhase && <StepDots group={group} />}

        <div className="co-ob__card">
          {(phase === 'loading' || phase === 'done') && (
            <p className="co-ob__sub" role="status">
              {phase === 'loading' ? 'Getting things ready…' : 'All set. Taking you to your dashboard…'}
            </p>
          )}

          {phase === 'loadError' && (
            <>
              <h1 className="co-ob__title">Something went wrong</h1>
              <p className="co-ob__sub">{error ?? 'We couldn’t load your onboarding.'}</p>
              <div className="co-ob__nav">
                <span />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => window.location.reload()}
                >
                  Try again
                </button>
              </div>
            </>
          )}

          {phase === 'name' && (
            <>
              <span className="co-ob__steplabel">{GROUP_LABELS[0]}</span>
              <h1 className="co-ob__title">First, who are we speaking to?</h1>
              <p className="co-ob__sub">
                Your name and job, so Reeve knows who it’s helping and how to pitch things.
              </p>
              <div className="co-ob__fields">
                <div className="field">
                  <label className="field__label" htmlFor="ob-first">
                    First name
                  </label>
                  <input
                    id="ob-first"
                    className="input"
                    type="text"
                    autoComplete="given-name"
                    placeholder="e.g. Sam"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="ob-last">
                    Last name
                  </label>
                  <input
                    id="ob-last"
                    className="input"
                    type="text"
                    autoComplete="family-name"
                    placeholder="e.g. Okonkwo"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="ob-role">
                    Your role
                  </label>
                  <input
                    id="ob-role"
                    className="input"
                    type="text"
                    autoComplete="organization-title"
                    placeholder="e.g. Founder, Office Manager"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {phase === 'company' && (
            <>
              <span className="co-ob__steplabel">{GROUP_LABELS[1]}</span>
              <h1 className="co-ob__title">
                {lookedUp ? 'Does this look right?' : 'Tell us about your company'}
              </h1>
              <p className="co-ob__sub">
                {lookedUp
                  ? 'From Companies House. Change anything that isn’t right.'
                  : 'The essentials, so Reeve works out the right requirements.'}
              </p>
              {lookupNote && (
                <p className="co-ob__note" role="status">
                  {lookupNote}
                </p>
              )}
              {lookedUp && !lookedUp.isActive && (
                <p className="co-ob__note" role="status">
                  Companies House shows this company as not active. Check the number if that’s a surprise.
                </p>
              )}
              <div className="co-ob__fields">
                <div className="field">
                  <label className="field__label" htmlFor="ob-coname">
                    Company name
                  </label>
                  <input
                    id="ob-coname"
                    className="input"
                    type="text"
                    placeholder="e.g. Bloom & Forage Ltd"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="ob-cotype">
                    Company type
                  </label>
                  <select
                    id="ob-cotype"
                    className="select"
                    value={companyType}
                    onChange={(e) => setCompanyType(e.target.value as CompanyType)}
                  >
                    <option value="" disabled>
                      Choose a type…
                    </option>
                    {COMPANY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {phase === 'companyNumber' && (
            <>
              <span className="co-ob__steplabel">{GROUP_LABELS[1]}</span>
              <h1 className="co-ob__title">Do you have a company number?</h1>
              <p className="co-ob__sub">
                If you give us your Companies House number, Reeve looks up your registered name, legal
                form and country, so there’s less for you to type and less to get wrong.
              </p>
              <div className="co-ob__fields">
                <div className="field">
                  <label className="field__label" htmlFor="ob-conum">
                    Company number
                  </label>
                  <input
                    id="ob-conum"
                    className="input"
                    type="text"
                    // NOT inputMode="numeric": Scottish and LLP numbers carry a letter prefix
                    // (SC016876, OC301540), which a numeric keypad cannot type.
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder="e.g. 00445790 or SC016876"
                    value={companyNumber}
                    onChange={(e) => setCompanyNumber(e.target.value)}
                  />
                  <span className="field__hint">
                    On your certificate of incorporation, or search Companies House by name.
                  </span>
                </div>
              </div>
            </>
          )}

          {phase === 'regionHeadcount' && (
            <>
              <span className="co-ob__steplabel">{GROUP_LABELS[2]}</span>
              <h1 className="co-ob__title">Where and how big?</h1>
              <p className="co-ob__sub">
                These set the legal scope, so Reeve applies the right rules.
              </p>
              <div className="co-ob__fields">
                <div className="field">
                  <span className="field__label">Which part of the UK do you mainly operate in?</span>
                  <div className="co-choices">
                    {REGION_OPTIONS.map((opt) => {
                      const selected = region === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`co-choice${selected ? ' is-selected' : ''}`}
                          aria-pressed={selected}
                          onClick={() => setRegion(opt.value)}
                        >
                          <span className="co-choice__radio" aria-hidden="true">
                            {selected && <Tick size={12} />}
                          </span>
                          <span>
                            <span className="co-choice__name">{opt.label}</span>
                            <span className="co-choice__sub">{opt.sub}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <span className="field__hint">Northern Ireland’s rules are devolved, not covered yet.</span>
                </div>

                <div className="field">
                  <span className="field__label">How many employees do you have?</span>
                  <div className="co-stepper">
                    <button
                      type="button"
                      className="co-stepper__btn"
                      aria-label="Fewer employees"
                      disabled={headcount <= 0}
                      onClick={() => setHeadcount((n) => Math.max(0, n - 1))}
                    >
                      &minus;
                    </button>
                    <span className="co-stepper__value" aria-live="polite">
                      {headcount}
                    </span>
                    <button
                      type="button"
                      className="co-stepper__btn"
                      aria-label="More employees"
                      disabled={headcount >= 10000}
                      onClick={() => setHeadcount((n) => n + 1)}
                    >
                      +
                    </button>
                  </div>
                  <span className="field__hint">
                    Count part-time, casual and zero-hours staff. Don&rsquo;t include yourself or any
                    unpaid directors.
                  </span>
                </div>
              </div>
            </>
          )}

          {phase === 'findings' && question && (
            <>
              <span className="co-ob__steplabel">{GROUP_LABELS[2]}</span>
              <h1 className="co-ob__title">{question.prompt}</h1>
              {question.help && <p className="co-ob__sub">{question.help}</p>}
              <div className="co-ob__fields">{renderQuestion(question, answer, setAnswer)}</div>
            </>
          )}

          {phase === 'groups' && groups[groupIndex] && (
            <>
              <span className="co-ob__steplabel">
                {groups[groupIndex].title} · {groupIndex + 1} of {groups.length}
              </span>
              <h1 className="co-ob__title">{groups[groupIndex].title}</h1>
              <p className="co-ob__sub">{groups[groupIndex].blurb}</p>

              <div className="co-ob__fields">
                {screenQuestions.map((q) => {
                  // Derived from the ancestry chain, never sent as a number, so the indent always
                  // matches the structure it is depicting.
                  const depth = questionDepth(q, screenQuestions);
                  return (
                  <div
                    className={`field${depth ? ' field--nested' : ''}${
                      revealing.has(q.key) ? ' field--revealing' : ''
                    }${leaving.has(q.key) ? ' field--leaving' : ''}`}
                    style={depth ? ({ '--q-depth': depth } as CSSProperties) : undefined}
                    key={q.key}
                  >
                    <span className="field__label">{q.prompt}</span>
                    {q.help && <span className="field__hint">{q.help}</span>}
                    {/* Only shown while the suggestion is still the reason a value is selected: once the
                        user picks for themselves, the register is no longer what the answer rests on. */}
                    {typeof q.value !== 'boolean' && q.suggestionEvidence && (
                      <span className="co-ob__sourced">
                        Filled in from {registerName(q.suggestionSource)} — {q.suggestionEvidence}. Change
                        it if it’s wrong.
                      </span>
                    )}
                    <div className="co-toggle" role="group" aria-label={q.prompt}>
                      <button
                        type="button"
                        className={`co-toggle__btn${shownValue(q) === true ? ' is-on' : ''}`}
                        aria-pressed={shownValue(q) === true}
                        disabled={savingKey !== null}
                        onClick={() => void answerGrouped(q.key, true)}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className={`co-toggle__btn${shownValue(q) === false ? ' is-on' : ''}`}
                        aria-pressed={shownValue(q) === false}
                        disabled={savingKey !== null}
                        onClick={() => void answerGrouped(q.key, false)}
                      >
                        No
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}

          {isFormPhase && (
            <>
              {error && (
                <p className="co-ob__error" role="alert">
                  {error}
                </p>
              )}
              <div className="co-ob__nav">
                {backPhase || canGoBackAQuestion || canGoBackAScreen ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={submitting}
                    onClick={() => {
                      if (canGoBackAScreen) {
                        setError(null);
                        setGroupIndex((n) => n - 1);
                        return;
                      }
                      if (canGoBackAQuestion) {
                        goBackOneQuestion();
                        return;
                      }
                      setError(null);
                      setPhase(backPhase!);
                    }}
                  >
                    Back
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!canContinue || submitting || lookingUp}
                  onClick={() => void onContinue()}
                >
                  {lookingUp
                    ? 'Looking you up…'
                    : submitting
                      ? 'Saving…'
                      : phase === 'companyNumber'
                        ? 'Look up my company'
                        : 'Continue'}
                  {!submitting && !lookingUp && <ArrowRight size={16} />}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Two first-class ways past the lookup. Sole traders and ordinary partnerships are not on
            Companies House at all, so "no number" is a normal answer, not a skip. */}
        {phase === 'companyNumber' && (
          <div className="co-ob__alts">
            <button
              type="button"
              className="co-ob__skip"
              disabled={submitting || lookingUp}
              onClick={skipLookup}
            >
              I don’t have a company number
            </button>
            <button
              type="button"
              className="co-ob__skip"
              disabled={submitting || lookingUp}
              onClick={skipLookup}
            >
              Set up manually
            </button>
          </div>
        )}

        {/* Localhost dev builds only, and stripped from the production bundle by the DEV check in
            canDevSeed. Deliberately styled as an obvious debug affordance, not a product control. */}
        {isFormPhase && canDevSeed() && (
          <button
            type="button"
            className="co-ob__dev"
            disabled={submitting || lookingUp}
            onClick={runDevSeed}
          >
            Dev: skip with a demo company
          </button>
        )}
      </div>
    </div>
  );
}

/** Render the input for a findings question by its `kind`. */
function renderQuestion(
  question: OnboardingQuestion,
  answer: string,
  setAnswer: (v: string) => void,
): ReactNode {
  switch (question.kind) {
    case 'longText':
      return (
        <div className="field">
          <textarea
            className="input"
            rows={4}
            style={{ resize: 'vertical', minHeight: 112 }}
            placeholder="Type your answer…"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        </div>
      );
    case 'number':
      return (
        <div className="field">
          <input
            className="input"
            type="number"
            inputMode="numeric"
            placeholder="0"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        </div>
      );
    case 'boolean':
      return (
        <div className="field">
          <div className="co-toggle" role="group" aria-label={question.prompt}>
            <button
              type="button"
              className={`co-toggle__btn${answer === 'true' ? ' is-on' : ''}`}
              aria-pressed={answer === 'true'}
              onClick={() => setAnswer('true')}
            >
              Yes
            </button>
            <button
              type="button"
              className={`co-toggle__btn${answer === 'false' ? ' is-on' : ''}`}
              aria-pressed={answer === 'false'}
              onClick={() => setAnswer('false')}
            >
              No
            </button>
          </div>
        </div>
      );
    case 'choice':
      return (
        <div className="co-choices">
          {(question.options ?? []).map((opt) => {
            const selected = answer === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`co-choice${selected ? ' is-selected' : ''}`}
                aria-pressed={selected}
                onClick={() => setAnswer(opt.value)}
              >
                <span className="co-choice__radio" aria-hidden="true">
                  {selected && <Tick size={12} />}
                </span>
                <span>
                  <span className="co-choice__name">{opt.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      );
    case 'text':
    default:
      return (
        <div className="field">
          <input
            className="input"
            type="text"
            placeholder="Type your answer…"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        </div>
      );
  }
}
